import type { Row } from '@tanstack/react-table';
import { disambiguatedPath } from '../findings-display-model';
import type { FindingsGroupBy } from '../findings-url';
import type { FindingDoc } from '../findings-types';
import { formatRuleLabel } from '@/lib/govern-rule-catalog';

export type EnrichedGroup = {
  key: string;
  label: string;
  rows: Row<FindingDoc>[];
  totalCount: number;
  openCount: number;
  fixableCount: number;
  openIds: string[];
  fixableIds: string[];
  severityCounts: { error: number; warning: number; info: number };
  categoryItems: Array<{ category: string; label: string; count: number }>;
  topFiles: Array<{ filePath: string; count: number }>;
  repoRef?: string;
};

function parseGroupJson(value: string | undefined): Record<string, unknown> {
  if (!value) return {};
  try {
    const parsed = JSON.parse(value) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

function groupString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function firstGroupString(...values: unknown[]): string | undefined {
  for (const value of values) {
    const text = groupString(value);
    if (text) return text;
  }
  return undefined;
}

function normalizeDecisionKeyPart(value: string | undefined): string {
  return (value ?? 'unknown')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[:|]/g, '/');
}

function groupRuleFamily(ruleId: string): string {
  const prefix = ruleId.split('/')[0];
  return prefix || 'other';
}

function decisionKeyForFinding(f: FindingDoc): string {
  const attributes = parseGroupJson(f.attributesJson);
  const evidence = parseGroupJson(f.evidenceJson);
  const groupId = firstGroupString(attributes.groupId, attributes.decisionKey);
  if (groupId) return `group:${normalizeDecisionKeyPart(groupId)}`;

  const classToken = firstGroupString(
    attributes.classToken,
    attributes.className,
    attributes.utility,
    attributes.tailwindClass,
    evidence.classToken,
    evidence.className,
    evidence.utility,
  );
  const resolvedToken = firstGroupString(
    attributes.resolvedToken,
    attributes.token,
    attributes.tokenName,
    evidence.resolvedToken,
    evidence.token,
    evidence.tokenName,
  );
  const rawValue = firstGroupString(
    f.rawValue,
    attributes.rawValue,
    attributes.value,
    evidence.rawValue,
    evidence.value,
  );
  const resolvedValue = firstGroupString(
    attributes.resolvedValue,
    evidence.resolvedValue,
    rawValue,
  );
  const suggestedTarget = firstGroupString(
    f.suggestedToken,
    f.suggestedComponentName,
    attributes.suggestedToken,
    attributes.suggestedTarget,
    attributes.canonicalComponent,
    evidence.suggestedToken,
  );
  const component = firstGroupString(
    f.suggestedComponentName,
    attributes.component,
    attributes.componentName,
    attributes.canonicalComponent,
    evidence.component,
  );
  const property = firstGroupString(
    f.prop,
    attributes.property,
    attributes.prop,
    evidence.property,
    f.category,
  );
  const policyDimension = normalizeDecisionKeyPart(
    firstGroupString(attributes.policyDimension, attributes.policyKey),
  );
  const rule = normalizeDecisionKeyPart(f.ruleId);

  if (f.ruleId.startsWith('tailwind/')) {
    return [
      'tailwind',
      rule,
      normalizeDecisionKeyPart(classToken ?? rawValue ?? resolvedToken),
      normalizeDecisionKeyPart(resolvedToken),
      normalizeDecisionKeyPart(resolvedValue),
      policyDimension,
    ].join(':');
  }
  if (f.ruleId.startsWith('components/') || f.ruleId.startsWith('props/')) {
    return [
      'components',
      rule,
      normalizeDecisionKeyPart(component ?? suggestedTarget),
      normalizeDecisionKeyPart(f.suggestedFrom),
      policyDimension,
    ].join(':');
  }
  if (f.ruleId.startsWith('a11y/')) {
    return [
      'a11y',
      rule,
      normalizeDecisionKeyPart(
        firstGroupString(attributes.issue, attributes.element, component),
      ),
      normalizeDecisionKeyPart(
        firstGroupString(attributes.role, attributes.name),
      ),
    ].join(':');
  }
  if (f.ruleId.startsWith('tokens/') || f.ruleId.startsWith('styles/')) {
    return [
      groupRuleFamily(f.ruleId),
      rule,
      normalizeDecisionKeyPart(rawValue ?? resolvedValue),
      normalizeDecisionKeyPart(property),
      normalizeDecisionKeyPart(suggestedTarget),
    ].join(':');
  }
  return `finding:${normalizeDecisionKeyPart(f.fingerprint)}`;
}

export function groupKeyFor(f: FindingDoc, mode: FindingsGroupBy): string {
  if (mode === 'file') return f.filePath ?? '(no file)';
  if (mode === 'rule') return f.ruleId;
  if (mode === 'area') return f.areaId ?? 'unassigned';
  if (mode === 'decision') return decisionKeyForFinding(f);
  if (f.prNumber != null) return `pr:${f.prNumber}`;
  if (f.commitSha) return `commit:${f.commitSha}`;
  return 'untracked';
}

export function groupLabelFor(key: string, mode: FindingsGroupBy): string {
  if (mode === 'file') return disambiguatedPath(key);
  if (mode === 'rule') return formatRuleLabel(key);
  // Area groups carry a server-provided display label (the area's human name);
  // this fallback is used only if that's missing. `key` is the areaId.
  if (mode === 'area') return key === 'unassigned' ? 'Unassigned' : key;
  if (mode === 'decision') return key;
  if (key.startsWith('pr:')) return `PR #${key.slice(3)}`;
  if (key.startsWith('commit:')) return `commit ${key.slice(7, 14)}`;
  return 'Untracked findings';
}
